import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Cpu, Wrench, Rocket } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerCTA } from "@/components/InnerCTA";
import { InnerBlock, Callout } from "@/components/InnerBlock";
import { SERVICES, SERVICE_LIST } from "@/data/services";
import { INDUSTRY_LIST } from "@/data/industries";
import { SOLUTION_LIST } from "@/data/solutions";
import { WORK_LIST } from "@/data/work";
import { WORK_INTRO } from "@/data/work";
import { Placeholder } from "@/components/Placeholder";
import { cn } from "@/lib/utils";

// Real card thumbnails for the Work index. Single-candidate sources only
// (no responsive srcset arms) — the SPA catch-all serves index.html with a
// 200 for any missing /assets/*.webp URL, and browsers silently render that
// as a broken image. Every id below has been verified on disk.
const WORK_THUMBS = {
  "cs-delivery-hero": {
    webp: "/assets/cs-delivery-hero.webp",
    fallback: "/assets/cs-delivery-hero.jpg",
    alt: "A Twasal Awasal driver handing a parcel to a customer in front of a branded red delivery van, with a mosque and mountains in the background.",
  },
  "cs-wms": {
    webp: "/assets/cs-wms.webp",
    fallback: "/assets/cs-wms.jpg",
    alt: "Warehouse interior at dusk with forklifts moving pallets between aisles, workers in high-visibility vests and loading docks in the background.",
  },
  "cs-erp": {
    webp: "/assets/cs-erp.webp",
    fallback: "/assets/cs-erp.jpg",
    alt: "A logistics control room overlooking a yard of trucks and shipping containers, with a curved bank of monitors showing route maps and fleet dashboards.",
  },
  "cs-showroom": {
    webp: "/assets/cs-showroom.webp",
    fallback: "/assets/cs-showroom.jpg",
    alt: "A vehicle showroom in Muscat with rows of SUVs and sedans on display, and customers in traditional Omani dress speaking with sales staff.",
  },
  "cs-marketplace": {
    webp: "/assets/cs-marketplace.webp",
    fallback: "/assets/cs-marketplace.jpg",
    alt: "A woman shopping on a laptop, viewing an online marketplace with product listings and a shopping cart on screen.",
  },
};

function WorkThumb({ id }) {
  const t = WORK_THUMBS[id];
  if (!t) return <Placeholder id={id} width={1200} height={800} />;
  return (
    <div className="overflow-hidden rounded-md bg-wafa-cream/40">
      <picture>
        <source type="image/webp" srcSet={t.webp} />
        <img
          src={t.fallback}
          alt={t.alt}
          width="1200"
          height="800"
          loading="lazy"
          decoding="async"
          data-testid={`work-thumb-${id}`}
          className="block h-auto w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />
      </picture>
    </div>
  );
}

/** Shared card renderer for every simple index page. */
function CardGrid({ items }) {
  return (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {items.map((it) => (
        <Link
          key={it.path}
          to={it.path}
          data-testid={`index-card-${it.slug}`}
          className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]"
        >
          <div className="font-heading text-lg font-semibold text-wafa-deep group-hover:text-wafa-primary">
            {it.navLabel || it.h1}
          </div>
          {it.intro ? (
            <p className="mt-3 line-clamp-3 text-sm text-wafa-ink/70">
              {it.intro.length > 160 ? it.intro.slice(0, 160) + "…" : it.intro}
            </p>
          ) : null}
          <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-deep">
            Read more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>
      ))}
    </div>
  );
}

function IndexShell({ testId, eyebrow, h1, intro, meta, extra, children, breadcrumbs, footer }) {
  return (
    <div data-testid={testId}>
      <Seo title={meta.title} description={meta.description} />
      <PageHero eyebrow={eyebrow} h1={h1} intro={intro} breadcrumbs={breadcrumbs} />
      {extra}
      <InnerBlock tint="white">
        {children}
      </InnerBlock>
      {footer}
      <InnerCTA heading="Not sure which one you need?" body="Book a call. We'll listen for ten minutes, then tell you where to start." />
    </div>
  );
}

// -------- Services index: four grouped blocks (Build · Intelligence · Secure · Run) ------

const SERVICE_GROUPS = [
  {
    key: "build",
    icon: Wrench,
    title: "Build",
    lede: "The software itself — designed, coded, tested, shipped.",
    slugs: [
      "custom-software-development-company-in-oman",
      "saas-development-company-in-oman",
      "enterprise-software-development-company-in-oman",
      "mobile-app-development-company-in-oman",
      "web-design-company-in-oman",
      "erp-software-development-company-in-oman",
      "crm-software-development-company-in-oman",
      "ui-ux-design-branding-oman",
    ],
  },
  {
    key: "intelligence",
    icon: Cpu,
    title: "Intelligence",
    lede: "Turning software into something that thinks, and turning traffic into leads.",
    slugs: ["digital-marketing-company-in-oman"],
    extraLinks: [
      { title: "AI Lab", desc: "AI that survives contact with production.", to: "/ai-lab" },
    ],
  },
  {
    key: "secure",
    icon: ShieldCheck,
    title: "Secure",
    lede: "Governance, risk and compliance — the evidence, not the intent.",
    slugs: [],
    extraLinks: [
      { title: "Meyar Enterprise GRC", desc: "Governance and risk, joined up.", to: "/meyar/enterprise-grc" },
      { title: "Meyar Cyber Security GRC", desc: "Controls, mapped to a framework auditors accept.", to: "/meyar/cyber-security-grc" },
    ],
  },
  {
    key: "run",
    icon: Rocket,
    title: "Run",
    lede: "Where it lives, how it stays up, and who answers when it does not.",
    slugs: ["cloud-devops-oman", "application-support-maintenance-oman"],
  },
];

function ServiceGroupCard({ label, desc, to }) {
  return (
    <Link
      to={to}
      data-testid={`service-group-link-${to.replace(/[^a-z0-9]+/gi, "-")}`}
      className="group flex items-start justify-between gap-3 rounded-lg border border-wafa-deep/10 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.2)]"
    >
      <div>
        <div className="font-heading text-sm font-semibold text-wafa-deep group-hover:text-wafa-primary">
          {label}
        </div>
        {desc ? <p className="mt-1 text-xs text-wafa-ink/70">{desc}</p> : null}
      </div>
      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-wafa-primary transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

function ServicesGrouped() {
  return (
    <div className="space-y-10">
      {SERVICE_GROUPS.map((g) => {
        const Icon = g.icon;
        const svcLinks = g.slugs
          .map((s) => SERVICES[s])
          .filter(Boolean)
          .map((s) => ({ label: s.navLabel || s.h1, desc: null, to: s.path }));
        const links = [...svcLinks, ...(g.extraLinks || []).map((e) => ({ label: e.title, desc: e.desc, to: e.to }))];
        return (
          <div key={g.key} data-testid={`service-group-${g.key}`} className="rounded-2xl border border-wafa-deep/10 bg-wafa-cream/30 p-6 lg:p-8">
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-wafa-primary/10 text-wafa-primary">
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-semibold tracking-tight text-wafa-deep sm:text-3xl">{g.title}</h3>
                <p className="mt-1 text-sm text-wafa-ink/75 lg:text-base">{g.lede}</p>
              </div>
            </div>
            <div className="mt-6 grid gap-3 md:grid-cols-2">
              {links.map((l, i) => (
                <ServiceGroupCard key={i} label={l.label} desc={l.desc} to={l.to} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ServicesIndex() {
  return (
    <IndexShell
      testId="services-index"
      eyebrow="Services"
      h1="What we do"
      intro="Build, Intelligence, Secure and Run. Most companies buy those from four different suppliers and spend their time working out whose fault a problem is. We do all four, and all four answer to the same person."
      meta={{ title: "Services — Wafa Solutions", description: "Four things, one delivery team in Oman: Build, Intelligence, Secure and Run." }}
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Services" }]}
    >
      <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-wafa-primary">
        Four things, one delivery team in Oman.
      </div>
      <ServicesGrouped />
      {/* Kept as a flat directory below for people who arrive scanning for a specific service. */}
      <div className="mt-16">
        <h3 className="font-heading text-lg font-semibold text-wafa-deep">All services, alphabetically</h3>
        <p className="mt-1 text-sm text-wafa-ink/60">For visitors who know what they are looking for.</p>
        <div className="mt-6">
          <CardGrid items={[...SERVICE_LIST].sort((a, b) => (a.navLabel || a.h1).localeCompare(b.navLabel || b.h1))} />
        </div>
      </div>
    </IndexShell>
  );
}

// ---------- Industries index --------------------------------------------------

export function IndustriesIndex() {
  return (
    <IndexShell
      testId="industries-index"
      eyebrow="Industries"
      h1="Industries we build for"
      intro="Software fails differently in every sector. A logistics platform breaks on dispatch and signal loss. A healthcare system breaks on consent and retention. A showroom breaks on consignment stock nobody owns. We build for the failure mode, not the category."
      meta={{ title: "Industries — Wafa Solutions", description: "The sectors our platforms and products are designed around." }}
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Industries" }]}
      footer={
        <InnerBlock tint="cream">
          <Callout>
            Where we already have platforms running, we say so on the page and name the platform. Where we do not, we tell you what we would build instead. You will not find invented client claims anywhere on this site.
          </Callout>
        </InnerBlock>
      }
    >
      <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-wafa-primary">
        The sectors our platforms and products are designed around.
      </div>
      <CardGrid items={INDUSTRY_LIST} />
    </IndexShell>
  );
}

// ---------- Solutions index ---------------------------------------------------

export function SolutionsIndex() {
  return (
    <IndexShell
      testId="solutions-index"
      eyebrow="Solutions"
      h1="Solutions ready to adapt"
      intro="Every platform below is one we have already put into production. The architecture is decided, the edge cases are found, and the expensive mistakes have been made — by us, on our own money. What you pay for is your brand, your business rules, and the parts genuinely specific to you. Nobody should be billed twice for the same problem."
      meta={{ title: "Solutions — Wafa Solutions", description: "Most of the hard part is already built. Bring your brand and business rules; skip the mistakes we've already made." }}
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Solutions" }]}
    >
      <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-wafa-primary">
        Most of the hard part is already built.
      </div>
      <CardGrid items={SOLUTION_LIST} />
    </IndexShell>
  );
}

// ---------- Work index --------------------------------------------------------

const STATUS_STYLES = {
  live: "bg-wafa-primary text-white",
  delivered: "bg-wafa-deep/10 text-wafa-deep",
  launching: "bg-wafa-sunshine text-wafa-deep",
};
const STATUS_LABELS = { live: "Live", delivered: "Delivered", launching: "Launching soon" };

export function WorkIndex() {
  return (
    <div data-testid="work-index">
      <Seo title="Work" description="Systems of ours that are running or about to. Written up properly." />
      <PageHero
        eyebrow="Work"
        h1="Systems of ours that are running right now."
        intro={WORK_INTRO}
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Work" }]}
      />
      <InnerBlock tint="white">
        <div className="grid gap-6 md:grid-cols-2">
          {WORK_LIST.map((cs) => (
            <Link
              key={cs.path}
              to={cs.path}
              data-testid={`work-card-${cs.slug}`}
              className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-4 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]"
            >
              <WorkThumb id={cs.thumbnailId} />
              <div className="p-3">
                <span className={cn("inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider", STATUS_STYLES[cs.statusKind] || "bg-wafa-cream text-wafa-deep")}>
                  {STATUS_LABELS[cs.statusKind] || cs.status}
                </span>
                <div className="mt-3 font-heading text-xl font-semibold text-wafa-deep group-hover:text-wafa-primary">
                  {cs.h1}
                </div>
                <p className="mt-2 text-sm text-wafa-ink/70 line-clamp-3">{cs.intro}</p>
                <div className="mt-4 text-xs uppercase tracking-wider text-wafa-ink/60">{cs.status}</div>
              </div>
            </Link>
          ))}
        </div>
      </InnerBlock>
      <InnerCTA heading="Want us to build the next one?" />
    </div>
  );
}

// ---------- Products index (never state a total count) -----------------------

export function ProductsIndex() {
  const twasalUrl = (process.env.REACT_APP_TWASAL_URL || "").trim();
  const products = [
    { title: "Meyar", desc: "Enterprise GRC and Cyber Security GRC.", to: "/meyar" },
    { title: "Delivery & logistics platform", desc: "Customer, driver, admin and shipper apps.", to: "/solutions/on-demand-delivery-platform" },
    { title: "Warehouse management", desc: "Receiving to dispatch, bin-level accuracy.", to: "/solutions/warehouse-management" },
    { title: "Showroom management", desc: "Vehicle inventory, sales, documents.", to: "/solutions/showroom-management" },
    { title: "Marketplace commerce", desc: "Multi-vendor, split orders, payouts.", to: "/solutions/e-commerce-marketplace" },
  ];
  return (
    <div data-testid="products-index">
      <Seo title="Products — Wafa Solutions" description="Software we own, running in production. And more in build." />
      <PageHero
        eyebrow="Products"
        h1="Software we own, running in production"
        intro="These are ours — designed, built and supported by us. Which means when we advise you on architecture or pricing, it comes from operating a live platform, not from reading about one. More are in build."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Products" }]}
      />
      <InnerBlock tint="white">
        <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-wafa-primary">
          We are a product company as much as a services one.
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <Link key={p.to} to={p.to} data-testid={`product-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25">
              <div className="font-heading text-lg font-semibold text-wafa-deep group-hover:text-wafa-primary">{p.title}</div>
              <p className="mt-3 text-sm text-wafa-ink/70">{p.desc}</p>
            </Link>
          ))}
          {/* Twasal Awasal — external link when REACT_APP_TWASAL_URL is set,
              otherwise an inert card. It will never link to an internal page. */}
          {twasalUrl ? (
            <a
              href={twasalUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-twasal-awasal"
              className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25"
            >
              <div className="flex items-center justify-between">
                <div className="font-heading text-lg font-semibold text-wafa-deep group-hover:text-wafa-primary">Twasal Awasal</div>
                <ArrowRight className="h-4 w-4 -rotate-45 text-wafa-primary" />
              </div>
              <p className="mt-3 text-sm text-wafa-ink/70">Multi-service delivery for Oman, in partnership with AlAwal Cargo Services. Launches on its own site.</p>
            </a>
          ) : (
            <div data-testid="product-twasal-awasal" className="flex flex-col rounded-lg border border-dashed border-wafa-deep/20 bg-wafa-cream/40 p-6">
              <div className="font-heading text-lg font-semibold text-wafa-deep">Twasal Awasal</div>
              <p className="mt-3 text-sm text-wafa-ink/70">Multi-service delivery for Oman, in partnership with AlAwal Cargo Services. Launching soon — the platform will have its own dedicated site.</p>
            </div>
          )}
          <div className="flex flex-col items-start justify-center rounded-lg border border-dashed border-wafa-deep/25 bg-wafa-cream/40 p-6">
            <div className="font-heading text-lg font-semibold text-wafa-deep">And more in build.</div>
            <p className="mt-3 text-sm text-wafa-ink/70">The pipeline never stops. Talk to us if you want to know what's next.</p>
          </div>
        </div>
      </InnerBlock>
      <InnerCTA heading="Want a demo?" body="Book a call and we'll walk you through whichever product is closest to your problem." />
    </div>
  );
}
